import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft, ChartBar, Funnel, Users, Envelope,
  CircleNotch, Phone, Notepad, CalendarCheck, TrendUp,
  Buildings, Target, ArrowsClockwise, Binoculars,
  MagnifyingGlass, Briefcase, Gavel, Bell, Star, Lightning,
  ChartPie, Clock, Trophy, Fire
} from '@phosphor-icons/react';

const STAGE_LABELS = {
  new: 'Nuevo',
  contacted: 'Contactado',
  interested: 'Interesado',
  qualified: 'Calificado',
  converted: 'Convertido',
  lost: 'Perdido',
};

const STAGE_COLORS = ['#94a3b8', '#3b82f6', '#f59e0b', '#8b5cf6', '#22c55e', '#ef4444'];

export default function B2BReportsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [pipelineData, setPipelineData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [sourcesData, setSourcesData] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [kpisData, setKpisData] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [overview, pipeline, activity, perf, campaigns, sources, conversion, kpis] = await Promise.all([
        axios.get(`${API_URL}/api/b2b/reports/overview`, { withCredentials: true }),
        axios.get(`${API_URL}/api/b2b/reports/pipeline`, { withCredentials: true }),
        axios.get(`${API_URL}/api/b2b/reports/activity`, { withCredentials: true }),
        axios.get(`${API_URL}/api/b2b/reports/performance`, { withCredentials: true }),
        axios.get(`${API_URL}/api/b2b/reports/campaigns`, { withCredentials: true }),
        axios.get(`${API_URL}/api/b2b/reports/sources`, { withCredentials: true }),
        axios.get(`${API_URL}/api/b2b/reports/conversion-time`, { withCredentials: true }),
        axios.get(`${API_URL}/api/b2b/market-intel/kpis`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setOverviewData(overview.data);
      setPipelineData(pipeline.data);
      setActivityData(activity.data);
      setPerformanceData(perf.data);
      setCampaignData(campaigns.data);
      setSourcesData(sources.data);
      setConversionData(conversion.data);
      setKpisData(kpis.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: ChartPie },
    { id: 'pipeline', label: 'Pipeline', icon: Funnel },
    { id: 'conversion', label: 'Conversion', icon: Clock },
    { id: 'activity', label: 'Actividad', icon: ChartBar },
    { id: 'performance', label: 'Rendimiento', icon: Users },
    { id: 'campaigns', label: 'Campanas', icon: Envelope },
    { id: 'sources', label: 'Fuentes', icon: Target },
    { id: 'market-intel', label: 'Inteligencia', icon: Binoculars },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="b2b-reports-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/prospects')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-prospects-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reportes Avanzados B2B</h1>
            <p className="text-sm text-slate-500">Analisis completo de prospeccion y rendimiento</p>
          </div>
        </div>
        <button onClick={fetchAll} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg" data-testid="refresh-reports-btn">
          <ArrowsClockwise size={20} />
        </button>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id ? 'bg-white shadow text-blue-600' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && overviewData && <OverviewReport data={overviewData} />}
      {activeTab === 'pipeline' && pipelineData && <PipelineReport data={pipelineData} />}
      {activeTab === 'conversion' && conversionData && <ConversionReport data={conversionData} />}
      {activeTab === 'activity' && activityData && <ActivityReport data={activityData} />}
      {activeTab === 'performance' && performanceData && <PerformanceReport data={performanceData} />}
      {activeTab === 'campaigns' && campaignData && <CampaignsReport data={campaignData} />}
      {activeTab === 'sources' && sourcesData && <SourcesReport data={sourcesData} />}
      {activeTab === 'market-intel' && kpisData && <MarketIntelKPIs data={kpisData} />}
    </div>
  );
}

/* ==================== OVERVIEW (NEW) ==================== */
function OverviewReport({ data }) {
  const kpis = [
    { label: 'Total Prospectos', value: data.total_prospects, icon: Briefcase, color: 'text-blue-600 bg-blue-50', border: 'border-blue-200' },
    { label: 'Tasa Conversion', value: `${data.conversion_rate}%`, icon: TrendUp, color: 'text-emerald-600 bg-emerald-50', border: 'border-emerald-200' },
    { label: 'Dias Prom. Conversion', value: data.avg_conversion_days, icon: Clock, color: 'text-violet-600 bg-violet-50', border: 'border-violet-200' },
    { label: 'Convertidos este Mes', value: data.converted_this_month, icon: Trophy, color: 'text-amber-600 bg-amber-50', border: 'border-amber-200' },
    { label: 'Contactados esta Semana', value: data.contacted_this_week, icon: Phone, color: 'text-teal-600 bg-teal-50', border: 'border-teal-200' },
    { label: 'Seguimientos Pendientes', value: data.followups_due, icon: Bell, color: data.followups_due > 0 ? 'text-red-600 bg-red-50' : 'text-slate-600 bg-slate-50', border: data.followups_due > 0 ? 'border-red-200' : 'border-slate-200' },
  ];

  const statusData = data.by_status || {};
  const tempData = data.by_temperature || {};
  const totalProspects = data.total_prospects || 1;

  return (
    <div className="space-y-6" data-testid="overview-report">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <div key={i} className={`bg-white rounded-xl border ${kpi.border} p-4`} data-testid={`overview-kpi-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 rounded-lg ${kpi.color}`}>
                  <Icon size={18} weight="fill" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{kpi.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{kpi.label}</p>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pipeline Status Breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Funnel size={16} className="text-blue-500" /> Distribucion por Estado
          </h3>
          <div className="space-y-3">
            {Object.entries(STAGE_LABELS).map(([key, label], idx) => {
              const count = statusData[key] || 0;
              const pct = totalProspects > 0 ? Math.round((count / totalProspects) * 100) : 0;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 w-24 text-right font-medium">{label}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden">
                    <div
                      className="h-full rounded-full flex items-center px-2 text-xs text-white font-semibold transition-all duration-500"
                      style={{ width: `${Math.max(pct, count > 0 ? 10 : 0)}%`, backgroundColor: STAGE_COLORS[idx] }}
                    >
                      {count > 0 && count}
                    </div>
                  </div>
                  <span className="text-xs text-slate-500 w-10 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Temperature + Campaign Stats */}
        <div className="space-y-4">
          {/* Temperature */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Fire size={16} className="text-orange-500" /> Temperatura de Prospectos
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: 'hot', label: 'Caliente', color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-50' },
                { key: 'warm', label: 'Tibio', color: 'bg-amber-500', textColor: 'text-amber-600', bgLight: 'bg-amber-50' },
                { key: 'cold', label: 'Frio', color: 'bg-blue-400', textColor: 'text-blue-600', bgLight: 'bg-blue-50' },
              ].map(t => (
                <div key={t.key} className={`${t.bgLight} rounded-xl p-4 text-center`}>
                  <p className={`text-3xl font-bold ${t.textColor}`}>{tempData[t.key] || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">{t.label}</p>
                  <div className={`h-1.5 ${t.color} rounded-full mt-2 mx-auto`} style={{ width: `${Math.max(((tempData[t.key] || 0) / totalProspects) * 100, 5)}%` }} />
                </div>
              ))}
            </div>
          </div>

          {/* Campaign Stats */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Envelope size={16} className="text-violet-500" /> Campanas & Outreach
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-violet-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-violet-700">{data.active_campaigns}</p>
                <p className="text-xs text-slate-500">Campanas Activas</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-blue-700">{data.active_enrollments}</p>
                <p className="text-xs text-slate-500">Inscripciones Activas</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-emerald-700">{data.total_emails_sent}</p>
                <p className="text-xs text-slate-500">Emails Enviados</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-slate-700">{data.created_this_month}</p>
                <p className="text-xs text-slate-500">Nuevos este Mes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Best Source */}
      {data.best_source && data.best_source.source !== 'N/A' && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-4">
          <div className="p-3 bg-emerald-100 rounded-xl">
            <Star size={24} className="text-emerald-600" weight="fill" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-900">Mejor Fuente de Prospectos</p>
            <p className="text-lg font-bold text-emerald-700">{data.best_source.source}</p>
            <p className="text-xs text-emerald-600">{data.best_source.converted} conversiones de {data.best_source.total} prospectos ({data.best_source.rate}% tasa)</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== CONVERSION TIME (NEW) ==================== */
function ConversionReport({ data }) {
  const { conversions, avg_conversion_days, by_source, total_converted } = data;

  return (
    <div className="space-y-6" data-testid="conversion-report">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total Convertidos</p>
          <p className="text-3xl font-bold text-emerald-600 mt-1">{total_converted}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Dias Prom. Conversion</p>
          <p className="text-3xl font-bold text-blue-600 mt-1">{avg_conversion_days}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Fuentes con Datos</p>
          <p className="text-3xl font-bold text-violet-600 mt-1">{by_source.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Prom. Actividades/Conv.</p>
          <p className="text-3xl font-bold text-amber-600 mt-1">
            {conversions.length > 0 ? Math.round(conversions.reduce((s, c) => s + c.total_activities, 0) / conversions.length) : 0}
          </p>
        </div>
      </div>

      {/* Conversion Time by Source */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Clock size={20} /> Tiempo de Conversion por Fuente
        </h2>
        {by_source.length === 0 ? (
          <p className="text-slate-500 text-sm">Sin datos de conversion aun. Convierte prospectos para ver metricas.</p>
        ) : (
          <div className="space-y-3">
            {by_source.map((s, i) => {
              const maxDays = Math.max(...by_source.map(x => x.avg_days), 1);
              const barColor = s.avg_days <= 7 ? 'bg-emerald-500' : s.avg_days <= 30 ? 'bg-blue-500' : s.avg_days <= 60 ? 'bg-amber-500' : 'bg-red-400';
              return (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-32 text-sm font-medium text-slate-700 text-right truncate">{s.source}</div>
                  <div className="flex-1 bg-slate-100 rounded-lg h-8 overflow-hidden">
                    <div
                      className={`h-full ${barColor} rounded-lg flex items-center px-3 text-white text-xs font-semibold transition-all duration-500`}
                      style={{ width: `${Math.max((s.avg_days / maxDays) * 100, 12)}%` }}
                    >
                      {s.avg_days} dias
                    </div>
                  </div>
                  <span className="text-xs text-slate-500 w-20 text-right">{s.count} conv.</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-4 mt-4 text-xs">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-emerald-500" /><span className="text-slate-500">&le;7 dias</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-blue-500" /><span className="text-slate-500">&le;30 dias</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-amber-500" /><span className="text-slate-500">&le;60 dias</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-red-400" /><span className="text-slate-500">&gt;60 dias</span></div>
        </div>
      </div>

      {/* Detailed Conversions Table */}
      {conversions.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendUp size={20} /> Detalle de Conversiones
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Empresa</th>
                  <th className="text-left p-3 font-medium text-slate-600">Fuente</th>
                  <th className="text-left p-3 font-medium text-slate-600">Industria</th>
                  <th className="text-center p-3 font-medium text-slate-600">Dias Total</th>
                  <th className="text-center p-3 font-medium text-slate-600">Dias a 1er Contacto</th>
                  <th className="text-center p-3 font-medium text-slate-600">Actividades</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {conversions.map((c, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-900">{c.company || '-'}</td>
                    <td className="p-3 text-slate-600">{c.source || '-'}</td>
                    <td className="p-3 text-slate-600">{c.industry || '-'}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        c.total_days <= 7 ? 'bg-emerald-100 text-emerald-700' :
                        c.total_days <= 30 ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {c.total_days}d
                      </span>
                    </td>
                    <td className="p-3 text-center text-slate-600">{c.days_to_first_contact !== null ? `${c.days_to_first_contact}d` : '-'}</td>
                    <td className="p-3 text-center font-semibold">{c.total_activities}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== PIPELINE ==================== */
function PipelineReport({ data }) {
  const { funnel, monthly } = data;
  const maxCount = Math.max(...funnel.map(f => f.count), 1);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
          <Funnel size={20} /> Embudo de Conversion
        </h2>
        <div className="space-y-3">
          {funnel.map((stage, idx) => (
            <div key={stage.stage} className="flex items-center gap-4">
              <div className="w-28 text-sm font-medium text-slate-700 text-right">
                {STAGE_LABELS[stage.stage]}
              </div>
              <div className="flex-1 h-10 bg-slate-100 rounded-lg overflow-hidden relative">
                <div
                  className="h-full rounded-lg transition-all duration-500 flex items-center px-3"
                  style={{
                    width: `${Math.max((stage.count / maxCount) * 100, 8)}%`,
                    backgroundColor: STAGE_COLORS[idx],
                  }}
                >
                  <span className="text-white text-sm font-bold">{stage.count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2">
          <TrendUp size={20} /> Tendencia Mensual
        </h2>
        {monthly.length > 0 ? (
          <>
            {/* Visual bar chart */}
            <div className="flex items-end gap-2 h-40 mb-4">
              {monthly.map((m, i) => {
                const maxCreated = Math.max(...monthly.map(x => x.created), 1);
                const createdH = (m.created / maxCreated) * 100;
                const convertedH = (m.converted / maxCreated) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex items-end justify-center gap-0.5 h-32">
                      <div className="w-5 bg-blue-400 rounded-t transition-all duration-500" style={{ height: `${Math.max(createdH, m.created > 0 ? 8 : 2)}%` }} title={`Creados: ${m.created}`} />
                      <div className="w-5 bg-emerald-500 rounded-t transition-all duration-500" style={{ height: `${Math.max(convertedH, m.converted > 0 ? 8 : 2)}%` }} title={`Convertidos: ${m.converted}`} />
                    </div>
                    <span className="text-[10px] text-slate-500 text-center leading-tight">{m.month}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 text-xs mb-4">
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-blue-400" /><span className="text-slate-500">Creados</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-emerald-500" /><span className="text-slate-500">Convertidos</span></div>
            </div>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600">Mes</th>
                    <th className="text-center p-3 font-medium text-slate-600">Creados</th>
                    <th className="text-center p-3 font-medium text-slate-600">Convertidos</th>
                    <th className="text-left p-3 font-medium text-slate-600">Conversion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {monthly.map(m => (
                    <tr key={m.month} className="hover:bg-slate-50">
                      <td className="p-3 font-medium text-slate-900">{m.month}</td>
                      <td className="p-3 text-center text-blue-600 font-semibold">{m.created}</td>
                      <td className="p-3 text-center text-green-600 font-semibold">{m.converted}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full max-w-[120px]">
                            <div className="h-full bg-green-500 rounded-full" style={{ width: `${m.created > 0 ? (m.converted / m.created * 100) : 0}%` }} />
                          </div>
                          <span className="text-xs text-slate-500">{m.created > 0 ? Math.round(m.converted / m.created * 100) : 0}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="text-slate-500 text-sm">No hay datos mensuales disponibles</p>
        )}
      </div>
    </div>
  );
}

/* ==================== ACTIVITY ==================== */
function ActivityReport({ data }) {
  const { totals, weekly } = data;
  const activityTypes = [
    { key: 'call', label: 'Llamadas', icon: Phone, color: 'bg-blue-500' },
    { key: 'email', label: 'Emails', icon: Envelope, color: 'bg-green-500' },
    { key: 'meeting', label: 'Reuniones', icon: CalendarCheck, color: 'bg-violet-500' },
    { key: 'note', label: 'Notas', icon: Notepad, color: 'bg-amber-500' },
  ];

  const totalActivities = activityTypes.reduce((s, t) => s + (totals[t.key] || 0), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {activityTypes.map(t => (
          <div key={t.key} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t.label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{totals[t.key] || 0}</p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.color} text-white`}>
                <t.icon size={22} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Distribucion de Actividades</h2>
        {totalActivities > 0 ? (
          <>
            <div className="h-8 flex rounded-lg overflow-hidden mb-4">
              {activityTypes.map(t => {
                const pct = ((totals[t.key] || 0) / totalActivities * 100);
                if (pct === 0) return null;
                return (
                  <div key={t.key} className={`${t.color} flex items-center justify-center text-white text-xs font-medium`} style={{ width: `${pct}%` }} title={`${t.label}: ${totals[t.key]}`}>
                    {pct > 10 ? `${Math.round(pct)}%` : ''}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-4">
              {activityTypes.map(t => (
                <div key={t.key} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-sm ${t.color}`} />
                  <span className="text-sm text-slate-600">{t.label}: {totals[t.key] || 0}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-slate-500 text-sm">Sin actividades registradas</p>
        )}
      </div>

      {weekly.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Actividad Semanal</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Semana</th>
                  <th className="text-center p-3 font-medium text-blue-600">Llamadas</th>
                  <th className="text-center p-3 font-medium text-green-600">Emails</th>
                  <th className="text-center p-3 font-medium text-violet-600">Reuniones</th>
                  <th className="text-center p-3 font-medium text-amber-600">Notas</th>
                  <th className="text-center p-3 font-medium text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {weekly.map(w => (
                  <tr key={w.week} className="hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-900">{w.week}</td>
                    <td className="p-3 text-center">{w.call || 0}</td>
                    <td className="p-3 text-center">{w.email || 0}</td>
                    <td className="p-3 text-center">{w.meeting || 0}</td>
                    <td className="p-3 text-center">{w.note || 0}</td>
                    <td className="p-3 text-center font-semibold">{(w.call || 0) + (w.email || 0) + (w.meeting || 0) + (w.note || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== PERFORMANCE ==================== */
function PerformanceReport({ data }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Users size={20} /> Rendimiento por Vendedor
        </h2>
        {data.length === 0 ? (
          <p className="text-slate-500 text-sm">No hay datos de rendimiento</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Vendedor</th>
                  <th className="text-center p-3 font-medium text-slate-600">Prospectos</th>
                  <th className="text-center p-3 font-medium text-blue-600">Contactados</th>
                  <th className="text-center p-3 font-medium text-green-600">Convertidos</th>
                  <th className="text-center p-3 font-medium text-red-600">Perdidos</th>
                  <th className="text-center p-3 font-medium text-slate-600">Conversion</th>
                  <th className="text-center p-3 font-medium text-slate-600">Actividades</th>
                  <th className="text-center p-3 font-medium text-slate-600">Emails</th>
                  <th className="text-center p-3 font-medium text-slate-600">Llamadas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.map(rep => (
                  <tr key={rep.email} className="hover:bg-slate-50">
                    <td className="p-3">
                      <div>
                        <p className="font-medium text-slate-900">{rep.name}</p>
                        <p className="text-xs text-slate-400">{rep.role}</p>
                      </div>
                    </td>
                    <td className="p-3 text-center font-semibold">{rep.total_prospects}</td>
                    <td className="p-3 text-center text-blue-600">{rep.contacted}</td>
                    <td className="p-3 text-center text-green-600 font-semibold">{rep.converted}</td>
                    <td className="p-3 text-center text-red-500">{rep.lost}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-slate-100 rounded-full">
                          <div className={`h-full rounded-full ${rep.conversion_rate > 50 ? 'bg-green-500' : rep.conversion_rate > 25 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${Math.min(rep.conversion_rate, 100)}%` }} />
                        </div>
                        <span className="text-xs font-medium">{rep.conversion_rate}%</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">{rep.total_activities}</td>
                    <td className="p-3 text-center">{rep.emails_sent}</td>
                    <td className="p-3 text-center">{rep.calls_made}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Trophy size={20} className="text-amber-500" /> Top Performers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.slice(0, 3).map((rep, idx) => (
              <div key={rep.email} className={`rounded-lg p-4 border ${idx === 0 ? 'border-amber-300 bg-amber-50' : idx === 1 ? 'border-slate-300 bg-slate-50' : 'border-orange-200 bg-orange-50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${idx === 0 ? 'bg-amber-400 text-white' : idx === 1 ? 'bg-slate-400 text-white' : 'bg-orange-400 text-white'}`}>
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{rep.name}</p>
                    <p className="text-sm text-slate-500">{rep.converted} conversiones | {rep.conversion_rate}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== CAMPAIGNS ==================== */
function CampaignsReport({ data }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Envelope size={20} /> Rendimiento de Campanas
      </h2>
      {data.length === 0 ? (
        <p className="text-slate-500 text-sm">No hay campanas creadas</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Campana</th>
                <th className="text-center p-3 font-medium text-slate-600">Estado</th>
                <th className="text-center p-3 font-medium text-slate-600">Pasos</th>
                <th className="text-center p-3 font-medium text-violet-600">Inscritos</th>
                <th className="text-center p-3 font-medium text-green-600">Activos</th>
                <th className="text-center p-3 font-medium text-blue-600">Completados</th>
                <th className="text-center p-3 font-medium text-amber-600">Emails Enviados</th>
                <th className="text-center p-3 font-medium text-slate-600">Tasa Completado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map(c => {
                const completionRate = c.total_enrolled > 0 ? Math.round(c.completed / c.total_enrolled * 100) : 0;
                return (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-900">{c.name}</td>
                    <td className="p-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        c.status === 'active' ? 'bg-green-100 text-green-700' :
                        c.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {c.status === 'active' ? 'Activa' : c.status === 'paused' ? 'Pausada' : 'Borrador'}
                      </span>
                    </td>
                    <td className="p-3 text-center">{c.steps_count}</td>
                    <td className="p-3 text-center text-violet-600 font-semibold">{c.total_enrolled}</td>
                    <td className="p-3 text-center text-green-600">{c.active}</td>
                    <td className="p-3 text-center text-blue-600">{c.completed}</td>
                    <td className="p-3 text-center text-amber-600 font-semibold">{c.total_sent}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-slate-100 rounded-full">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${completionRate}%` }} />
                        </div>
                        <span className="text-xs font-medium">{completionRate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== SOURCES ==================== */
function SourcesReport({ data }) {
  const { by_source, by_industry } = data;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Target size={20} /> Conversion por Fuente
        </h2>
        {by_source.length === 0 ? (
          <p className="text-slate-500 text-sm">Sin datos</p>
        ) : (
          <div className="space-y-3">
            {by_source.sort((a, b) => b.total - a.total).map(s => (
              <div key={s.source} className="flex items-center gap-4">
                <div className="w-32 text-sm font-medium text-slate-700 text-right truncate">{s.source}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden flex">
                      <div className="h-full bg-blue-400 flex items-center justify-center text-xs text-white font-medium" style={{ width: `${s.total > 0 ? ((s.total - s.converted) / s.total * 100) : 0}%` }}>
                        {s.total - s.converted > 0 ? s.total - s.converted : ''}
                      </div>
                      <div className="h-full bg-green-500 flex items-center justify-center text-xs text-white font-medium" style={{ width: `${s.rate}%` }}>
                        {s.converted > 0 ? s.converted : ''}
                      </div>
                    </div>
                    <span className="text-sm text-slate-700 w-20 text-right">{s.total} total</span>
                    <span className="text-sm font-semibold text-green-600 w-14 text-right">{s.rate}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-4 mt-4 text-xs">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-blue-400" /><span className="text-slate-500">Sin convertir</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-green-500" /><span className="text-slate-500">Convertidos</span></div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Buildings size={20} /> Conversion por Industria
        </h2>
        {by_industry.length === 0 ? (
          <p className="text-slate-500 text-sm">Sin datos de industria</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Industria</th>
                  <th className="text-center p-3 font-medium text-slate-600">Total</th>
                  <th className="text-center p-3 font-medium text-green-600">Convertidos</th>
                  <th className="text-left p-3 font-medium text-slate-600">Tasa Conversion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {by_industry.sort((a, b) => b.rate - a.rate).map(ind => (
                  <tr key={ind.industry} className="hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-900">{ind.industry}</td>
                    <td className="p-3 text-center">{ind.total}</td>
                    <td className="p-3 text-center text-green-600 font-semibold">{ind.converted}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full max-w-[120px]">
                          <div className={`h-full rounded-full ${ind.rate > 50 ? 'bg-green-500' : ind.rate > 25 ? 'bg-amber-400' : 'bg-slate-300'}`} style={{ width: `${ind.rate}%` }} />
                        </div>
                        <span className="text-xs font-medium">{ind.rate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== MARKET INTEL KPIs ==================== */
function MarketIntelKPIs({ data }) {
  const s = data.summary || {};
  const scoreStats = data.score_stats || {};
  const scoreDist = data.score_distribution || [];
  const topIndustries = data.top_industries || [];
  const topStates = data.top_states || [];
  const topAnalyses = data.top_analyses || [];
  const alertStatus = data.alert_status || {};

  const maxDist = Math.max(...scoreDist.map(d => d.count), 1);
  const maxInd = Math.max(...topIndustries.map(i => i.count), 1);

  const kpiCards = [
    { label: 'Empresas Analizadas', value: s.total_analyses, sub: `${s.analyses_7d || 0} ultimos 7 dias`, icon: MagnifyingGlass, color: 'text-blue-600 bg-blue-50' },
    { label: 'Prospectos Generados', value: s.total_generated_prospects, sub: `${s.mi_prospects || 0} directos + ${s.similar_prospects || 0} similares + ${s.bid_prospects || 0} licitaciones`, icon: Briefcase, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'GCs Descubiertos', value: s.total_discovered_companies, sub: `${s.total_discoveries || 0} busquedas`, icon: Lightning, color: 'text-purple-600 bg-purple-50' },
    { label: 'Licitaciones Encontradas', value: s.total_bid_opportunities, sub: `${s.total_bid_searches || 0} busquedas | ${s.automated_alerts_sent || 0} alertas`, icon: Gavel, color: 'text-amber-600 bg-amber-50' },
    { label: 'Score Promedio', value: `${scoreStats.average || 0}%`, sub: `Min: ${scoreStats.min || 0}% | Max: ${scoreStats.max || 0}%`, icon: Star, color: 'text-pink-600 bg-pink-50' },
    { label: 'Tasa de Conversion', value: `${s.conversion_rate || 0}%`, sub: `${s.converted_count || 0} de ${s.total_generated_prospects || 0} convertidos`, icon: TrendUp, color: 'text-teal-600 bg-teal-50' },
  ];

  return (
    <div className="space-y-6" data-testid="market-intel-kpis">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpiCards.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`kpi-card-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{kpi.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{kpi.value}</p>
                  <p className="text-xs text-slate-400 mt-1">{kpi.sub}</p>
                </div>
                <div className={`p-2.5 rounded-lg ${kpi.color}`}>
                  <Icon size={20} weight="fill" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Star size={16} className="text-amber-500" /> Distribucion de Compatibilidad
          </h3>
          <div className="space-y-2.5">
            {scoreDist.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-14 text-right font-medium">{d.label}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                  <div
                    className={`h-full rounded-full flex items-center pl-2 text-xs text-white font-medium ${
                      i === 0 ? 'bg-emerald-500' : i === 1 ? 'bg-blue-500' : i === 2 ? 'bg-amber-500' : i === 3 ? 'bg-orange-500' : 'bg-red-400'
                    }`}
                    style={{ width: `${Math.max((d.count / maxDist) * 100, d.count > 0 ? 12 : 0)}%` }}
                  >
                    {d.count > 0 ? d.count : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Buildings size={16} className="text-blue-500" /> Industrias Analizadas
          </h3>
          {topIndustries.length === 0 ? (
            <p className="text-sm text-slate-400">Sin datos aun</p>
          ) : (
            <div className="space-y-2.5">
              {topIndustries.map((ind, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 w-32 text-right truncate font-medium">{ind.industry}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full flex items-center pl-2 text-[10px] text-white font-medium" style={{ width: `${Math.max((ind.count / maxInd) * 100, 12)}%` }}>
                      {ind.count}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendUp size={16} className="text-emerald-500" /> Top Empresas Compatibles (70%+)
          </h3>
          {topAnalyses.length === 0 ? (
            <p className="text-sm text-slate-400">Sin analisis de alta compatibilidad aun</p>
          ) : (
            <div className="space-y-2">
              {topAnalyses.map((a, i) => {
                const score = a.analysis?.compatibility_score || 0;
                const scoreColor = score >= 80 ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50';
                return (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg" data-testid={`top-analysis-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{a.analysis?.company_name || a.url}</p>
                      <p className="text-xs text-slate-400">{a.analysis?.industry || 'N/A'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${scoreColor}`}>{score}%</span>
                      {a.status === 'prospect_created' && (
                        <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[10px]">Prospecto</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Bell size={16} className="text-amber-500" /> Estado de Alertas
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-400">Estado</p>
                <p className={`text-sm font-bold ${alertStatus.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {alertStatus.enabled ? 'Activo' : 'Inactivo'}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-400">Frecuencia</p>
                <p className="text-sm font-bold text-slate-800 capitalize">{alertStatus.frequency || 'N/A'}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-400">Destinatarios</p>
                <p className="text-sm font-bold text-slate-800">{alertStatus.recipients_count || 0}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-400">Ultima Ejecucion</p>
                <p className="text-sm font-bold text-slate-800">
                  {alertStatus.last_run ? new Date(alertStatus.last_run).toLocaleDateString('es') : 'Nunca'}
                </p>
              </div>
            </div>
          </div>

          {topStates.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Target size={16} className="text-purple-500" /> Estados Mas Buscados
              </h3>
              <div className="flex flex-wrap gap-2">
                {topStates.map((st, i) => (
                  <span key={i} className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                    {st.state} ({st.count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
